import { useEffect, useState, useRef } from 'react';
import { MobileHeader, HeaderAction } from '../components/MobileHeader';
import { useSearchParams } from 'react-router-dom';
import { Edit2, Plus, Trash2, MapPin, Save, X, Calendar, RefreshCw, CheckCircle, AlertCircle, Download, Upload, Shield, UserPlus, Mail, Globe2, Link, Check, XCircle, Eye, EyeOff, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import { cn } from '../lib/utils';
import { ApiAdminPanel } from './ApiAdminPanel';
import PlannerPublic from './PlannerPublic';
import EntryList from '../components/memo/EntryList';
import DuplicatesPanel from '../components/profile/DuplicatesPanel';
import ProcessDiagram from '../components/profile/ProcessDiagram';
import TrashPanel from '../components/profile/TrashPanel';

const STYLES = ['Bachata', 'Brazilian Zouk', 'Salsa', 'Kizomba', 'Tango', 'Contemporain'];
const CITIES = ['Genève', 'Lausanne', 'Berne', 'Zurich', 'Fribourg'];

const PROFILE_SECTIONS_DEFAULT = [
  { id: 'claude_process', label: 'Process de Claude', adminOnly: true },
  { id: 'docs', label: 'Guide d\'utilisation' },
  { id: 'nav_config', label: 'Navigation personnalisée', adminOnly: true },
  { id: 'pricing_global', label: 'Tarification' },
  { id: 'formula', label: 'Formule CA' },
  { id: 'matrix', label: 'Matrice Tarifaire' },
  { id: 'venues', label: 'Lieux' },
  { id: 'gcal', label: 'Agenda & Tâches' },
  { id: 'export', label: 'Sauvegarde' },
  { id: 'access', label: 'Gestion des accès', adminOnly: true },
  { id: 'demo', label: 'Lien démo', adminOnly: true },
  { id: 'portal', label: 'Portail', adminOnly: true },
  { id: 'registrations', label: 'Inscriptions portail', adminOnly: true },
  { id: 'duplicates', label: "Doublons d'élèves", adminOnly: true },
  { id: 'trash', label: 'Corbeille', adminOnly: true },
  { id: 'planner', label: 'Planificateur', adminOnly: true },
  { id: 'api', label: 'API externe', adminOnly: true },
];

function useSectionState() {
  const { settings, updateSettings } = useAuth();
  const defaultOrder = PROFILE_SECTIONS_DEFAULT.map(s => s.id);

  const [order, setOrder] = useState(() =>
    settings.profile_section_order || defaultOrder
  );
  const [hidden, setHidden] = useState(() =>
    new Set(settings.profile_section_hidden || [])
  );

  // Sync quand les settings DB arrivent (après la connexion)
  useEffect(() => {
    if (settings.profile_section_order) setOrder(settings.profile_section_order);
    if (settings.profile_section_hidden) setHidden(new Set(settings.profile_section_hidden));
  }, [settings.profile_section_order, settings.profile_section_hidden]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = (newOrder, newHidden) => {
    updateSettings({
      profile_section_order:  newOrder,
      profile_section_hidden: [...newHidden],
    });
  };

  const toggleHidden = (id) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persist(order, next);
      return next;
    });
  };

  const moveUp = (id) => {
    setOrder(prev => {
      const idx = prev.indexOf(id);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      persist(next, hidden);
      return next;
    });
  };

  const moveDown = (id) => {
    setOrder(prev => {
      const idx = prev.indexOf(id);
      if (idx === -1 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      persist(next, hidden);
      return next;
    });
  };

  return { order, hidden, toggleHidden, moveUp, moveDown };
}

const MOBILE_QUERY = '(max-width: 1023px)';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

function SectionWrapper({ orderIdx = 0, label, hidden, onToggle, onMoveUp, onMoveDown, isFirst, isLast, children }) {
  const isMobile = useIsMobile();
  // Mobile : sections repliées par défaut (accordéon), pour ne pas avoir une page de 15 écrans
  const [expanded, setExpanded] = useState(false);
  if (isMobile && !hidden && !expanded) {
    return (
      <button style={{ order: orderIdx }} onClick={() => setExpanded(true)}
        className="w-full flex items-center justify-between px-4 h-14 bg-white rounded-2xl border border-slate-100 shadow-sm mb-2 active:bg-slate-50"
        data-testid="profile-section-collapsed">
        <span className="text-base font-semibold text-slate-800">{label}</span>
        <ChevronDown size={18} className="text-slate-400" />
      </button>
    );
  }
  if (hidden) {
    return (
      <div style={{ order: orderIdx }} className="flex items-center justify-between px-4 py-2.5 bg-slate-50 rounded-2xl border border-dashed border-slate-200 mb-2">
        <span className="text-sm font-semibold text-slate-400">{label}</span>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp} disabled={isFirst} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-200 disabled:opacity-30 transition-colors" title="Monter">
            <ChevronUp size={14} className="text-slate-500" />
          </button>
          <button onClick={onMoveDown} disabled={isLast} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-200 disabled:opacity-30 transition-colors" title="Descendre">
            <ChevronDown size={14} className="text-slate-500" />
          </button>
          <button onClick={onToggle} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-200 transition-colors" title="Afficher">
            <Eye size={14} className="text-slate-400" />
          </button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ order: orderIdx }} className="relative group/sec">
      {isMobile && (
        <button onClick={() => setExpanded(false)}
          className="w-full flex items-center justify-between px-4 h-12 mb-2 rounded-2xl bg-[#0288D1]/5 text-[#0288D1] active:bg-[#0288D1]/10">
          <span className="text-base font-semibold">{label}</span>
          <ChevronUp size={18} />
        </button>
      )}
      {/* Controls: always visible on mobile, hover on desktop */}
      <div className="absolute right-2 top-16 lg:top-2 z-20 flex items-center gap-0.5 opacity-100 md:opacity-0 md:group-hover/sec:opacity-100 transition-opacity bg-white/80 backdrop-blur-sm rounded-lg px-1 py-0.5 shadow-sm border border-slate-100">
        <button onClick={onMoveUp} disabled={isFirst} className="w-7 h-7 md:w-6 md:h-6 flex items-center justify-center rounded hover:bg-slate-100 disabled:opacity-30 transition-colors" title="Monter">
          <ChevronUp size={12} className="text-slate-500" />
        </button>
        <button onClick={onMoveDown} disabled={isLast} className="w-7 h-7 md:w-6 md:h-6 flex items-center justify-center rounded hover:bg-slate-100 disabled:opacity-30 transition-colors" title="Descendre">
          <ChevronDown size={12} className="text-slate-500" />
        </button>
        <button onClick={onToggle} className="w-7 h-7 md:w-6 md:h-6 flex items-center justify-center rounded hover:bg-slate-100 transition-colors" title="Masquer cette section">
          <EyeOff size={12} className="text-slate-400" />
        </button>
      </div>
      {children}
    </div>
  );
}

// ── Pricing Matrix Component ──────────────────────────────────────────────────
function PricingMatrixSection() {
  const [matrix, setMatrix] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [addingRow, setAddingRow] = useState(false);
  const [saving, setSaving] = useState({});

  const fetchMatrix = async () => {
    const [m, c] = await Promise.all([
      api.get('/pricing-matrix'),
      api.get('/group-courses'),
    ]);
    setMatrix(m || []);
    setCourses((c || []).filter(g => g.is_active !== false));
    setLoading(false);
  };

  useEffect(() => { fetchMatrix(); }, []);

  const handleAddRow = async () => {
    if (!newStatus.trim()) return;
    try {
      const row = await api.post('/pricing-matrix', { status_name: newStatus.trim(), course_prices: {} });
      setMatrix(prev => [...prev, row]);
      setNewStatus(''); setAddingRow(false);
    } catch (e) { toast.error(e.message || 'Erreur'); }
  };

  const handleDeleteRow = async (id) => {
    if (!window.confirm('Supprimer cette ligne tarifaire ?')) return;
    await api.delete(`/pricing-matrix/${id}`);
    setMatrix(prev => prev.filter(r => r.id !== id));
    toast.success('Ligne supprimée');
  };

  const handlePriceChange = async (rowId, courseKey, value) => {
    const price = value === '' ? null : parseFloat(value);
    setSaving(s => ({ ...s, [`${rowId}-${courseKey}`]: true }));
    try {
      const updated = await api.patch(`/pricing-matrix/${rowId}/price`, { course_key: courseKey, price });
      setMatrix(prev => prev.map(r => r.id === rowId ? updated : r));
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(s => ({ ...s, [`${rowId}-${courseKey}`]: false })); }
  };

  const allCols = [
    ...courses.map(c => ({ key: c.id, label: c.name || c.title || 'Cours' })),
    { key: '_private', label: 'Cours Privés' },
    { key: '_private_2p', label: 'Privés à 2' },  // v2.30 : prix horaire à deux (vide → tarif de base à deux)
  ];

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4" data-testid="pricing-matrix-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Matrice Tarifaire</div>
        <button onClick={() => setAddingRow(true)}
          className="flex items-center gap-1 text-xs text-[#0288D1] font-semibold hover:bg-[#0288D1]/5 px-2 py-1 rounded-lg transition-colors"
          data-testid="add-pricing-row-btn">
          <Plus size={13} /> Ajouter statut
        </button>
      </div>

      {/* Explication logique 3 niveaux */}
      <div className="mb-4 bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 text-xs text-slate-600 space-y-1.5">
        <p className="font-bold text-slate-700 mb-1">Résolution tarifaire automatique — 3 niveaux</p>
        <div className="flex gap-2"><span className="font-bold text-[#0288D1] shrink-0">N1</span><span><strong>Matrice (ci-dessous)</strong> — prix issu du statut de l'élève (ex. Standard, Aide) croisé avec le cours. C'est la <em>valeur figée</em> lors de la présence.</span></div>
        <div className="flex gap-2"><span className="font-bold text-amber-500 shrink-0">N2</span><span><strong>Surclassement inscription</strong> — prix fixe par élève/cours, s'il est défini (accessible via la fiche du cours).</span></div>
        <div className="flex gap-2"><span className="font-bold text-slate-400 shrink-0">N3</span><span><strong>Prix par défaut du cours</strong> — fallback global si aucun profil matriciel ne correspond.</span></div>
        <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-200">Les <strong>exceptions manuelles</strong> sur une présence passée (colonne CHF dans la fiche cours) priment toujours sur tous les niveaux.</p>
      </div>

      {loading ? (
        <div className="text-center py-4 text-slate-400 text-sm">Chargement…</div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left text-xs font-bold text-slate-500 py-2 pr-2 pl-1 whitespace-nowrap">Statut</th>
                <th className="text-center text-xs font-bold text-slate-500 py-2 px-2 whitespace-nowrap" title="Code court affiché dans la grille de présences (ex: 01, 02)">
                  Code
                </th>
                {allCols.map(col => (
                  <th key={col.key} className="text-center text-xs font-semibold text-slate-500 py-2 px-2 whitespace-nowrap max-w-[90px]">
                    <div className="truncate max-w-[80px]" title={col.label}>{col.label}</div>
                  </th>
                ))}
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {matrix.map(row => (
                <tr key={row.id} className="border-t border-slate-50">
                  <td className="py-2 pr-2 pl-1 font-bold text-slate-800 whitespace-nowrap">{row.status_name}</td>
                  <td className="text-center px-1 py-1">
                    <input
                      type="text"
                      maxLength={4}
                      placeholder="—"
                      defaultValue={row.status_code ?? ''}
                      onBlur={async e => {
                        const code = e.target.value.trim().slice(0, 4);
                        setSaving(s => ({ ...s, [`${row.id}-code`]: true }));
                        try {
                          const updated = await api.put(`/pricing-matrix/${row.id}`, { status_code: code || null });
                          setMatrix(prev => prev.map(r => r.id === row.id ? updated : r));
                        } catch { toast.error('Erreur sauvegarde code'); }
                        finally { setSaving(s => ({ ...s, [`${row.id}-code`]: false })); }
                      }}
                      className="w-12 h-8 text-center text-sm font-bold border border-slate-200 rounded-lg outline-none focus:border-[#0288D1] focus:ring-2 focus:ring-[#0288D1]/20 text-[#0288D1]"
                      data-testid={`code-${row.id}`}
                    />
                  </td>
                  {allCols.map(col => {
                    const v = (row.course_prices || {})[col.key];
                    const k = `${row.id}-${col.key}`;
                    return (
                      <td key={col.key} className="text-center px-1 py-1">
                        <div className="flex items-center justify-center gap-0.5">
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            placeholder="—"
                            defaultValue={v ?? ''}
                            onBlur={e => handlePriceChange(row.id, col.key, e.target.value)}
                            className="w-14 h-8 text-center text-sm border border-slate-200 rounded-lg outline-none focus:border-[#0288D1] focus:ring-2 focus:ring-[#0288D1]/20"
                            data-testid={`price-${row.id}-${col.key}`}
                          />
                          {saving[k] && <span className="text-[10px] text-slate-300">•</span>}
                        </div>
                      </td>
                    );
                  })}
                  <td className="pl-2">
                    <button onClick={() => handleDeleteRow(row.id)}
                      className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-red-400 rounded transition-colors"
                      data-testid={`delete-pricing-row-${row.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {matrix.length === 0 && (
            <div className="text-center py-4 text-slate-400 text-sm">Aucun statut — cliquez "Ajouter statut"</div>
          )}
        </div>
      )}

      {addingRow && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <input
            autoFocus
            value={newStatus}
            onChange={e => setNewStatus(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddRow(); if (e.key === 'Escape') setAddingRow(false); }}
            placeholder="Nom du statut (ex: Étudiant)"
            className="flex-1 h-9 px-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-[#0288D1]"
            data-testid="new-status-name-input"
          />
          <button onClick={handleAddRow} className="h-9 px-3 bg-[#0288D1] text-white text-sm font-bold rounded-xl">Créer</button>
          <button onClick={() => setAddingRow(false)} className="h-9 px-3 bg-slate-100 text-slate-600 text-sm rounded-xl">Annuler</button>
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-3">
        Les prix sont figés au moment de l'enregistrement d'une présence. Modifier un prix n'affecte pas les présences passées.
      </p>
    </div>
  );
}

export default function TeacherProfile() {
  const { t } = useLang();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showLocForm, setShowLocForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gcalStatus, setGcalStatus] = useState(null);
  const [gcalSyncing, setGcalSyncing] = useState(false);
  const [gcalCalendars, setGcalCalendars] = useState([]);
  const [gcalCalId, setGcalCalId] = useState('');
  const [gtasksStatus, setGtasksStatus] = useState(null);

  const fetchData = async () => {
    // v2.29 : une demande qui échoue (réseau faible) ne bloque plus la page sur « Chargement »
    let p, l, gcal, gtasks;
    try {
      [p, l, gcal, gtasks] = await Promise.all([
        api.get('/teacher'),
        api.get('/locations').catch(() => []),
        api.get('/auth/google/status').catch(() => ({ connected: false })),
        api.get('/gtasks/status').catch(() => ({ connected: false })),
      ]);
    } catch {
      setLoading(false);
      return;
    }
    setProfile(p);
    setLocations(l);
    setForm({ ...p, pricing: { ...p.pricing } });
    setGcalStatus(gcal);
    setGtasksStatus(gtasks);
    setGcalCalId(p?.gcal_calendar_id || '');
    if (gcal?.connected) {
      api.get('/auth/google/calendars').then(d => setGcalCalendars(d.calendars || [])).catch(() => {});
    }
    setLoading(false);
  };

  const { order: sectionOrder, hidden: hiddenSections, toggleHidden, moveUp, moveDown } = useSectionState();

  const sectionProps = (id) => {
    const allVisible = sectionOrder.filter(sid => {
      const def = PROFILE_SECTIONS_DEFAULT.find(s => s.id === sid);
      return !(def?.adminOnly && currentUser?.role !== 'admin');
    });
    const idx = sectionOrder.indexOf(id);
    const visIdx = allVisible.indexOf(id);
    return {
      orderIdx: idx >= 0 ? idx : 99,
      label: PROFILE_SECTIONS_DEFAULT.find(s => s.id === id)?.label || id,
      hidden: hiddenSections.has(id),
      onToggle: () => toggleHidden(id),
      onMoveUp: () => moveUp(id),
      onMoveDown: () => moveDown(id),
      isFirst: visIdx === 0,
      isLast: visIdx === allVisible.length - 1,
    };
  };

  useEffect(() => {
    fetchData();
    // Handle OAuth callback params
    if (searchParams.get('gcal_connected') === '1') {
      toast.success('Google Agenda connecté avec succès !');
      setSearchParams({});
    }
    if (searchParams.get('gcal_error') === '1') {
      toast.error('Erreur lors de la connexion Google Agenda');
      setSearchParams({});
    }
  }, []);

  const handleSave = async () => {
    const updated = await api.put('/teacher', form);
    setProfile(updated);
    setEditing(false);
    toast.success('Profil enregistré');
  };

  const handleGcalConnect = async () => {
    // Use tasks endpoint only when already connected (to extend scope)
    const endpoint = (gcalStatus?.connected && !gtasksStatus?.connected)
      ? '/auth/google/login-tasks'
      : '/auth/google/login';
    const data = await api.get(endpoint);
    if (data?.authorization_url) window.location.href = data.authorization_url;
  };

  const handleGcalDisconnect = async () => {
    if (!window.confirm('Déconnecter Google Agenda ?')) return;
    await api.delete('/auth/google/disconnect');
    setGcalStatus({ connected: false });
    toast.success('Google Agenda déconnecté');
  };

  const handleGcalCalendarChange = async (calId) => {
    setGcalCalId(calId);
    await api.put('/teacher', { gcal_calendar_id: calId });
    toast.success('Calendrier de synchronisation mis à jour');
  };

  const handleGcalSync = async () => {
    setGcalSyncing(true);
    try {
      const res = await api.post('/gcal/sync', {});
      toast.success(`Synchronisation terminée — ${res.synced} cours envoyés`);
    } catch {
      toast.error('Erreur de synchronisation');
    } finally { setGcalSyncing(false); }
  };

  const importRef = useRef(null);

  const handleExport = async () => {
    try {
      const data = await api.get('/admin/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bachata-desk-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch { toast.error('Erreur lors de l\'export'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm('Importer ces données ? Cela remplacera toutes les données existantes.')) {
      e.target.value = '';
      return;
    }
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await api.post('/admin/import', data);
      const total = Object.values(res.imported).reduce((a, b) => a + b, 0);
      toast.success(`Import réussi — ${total} documents importés`);
      e.target.value = '';
    } catch { toast.error('Erreur lors de l\'import — vérifiez le fichier'); e.target.value = ''; }
  };

  const toggleStyle = (style) => {
    setForm(f => ({
      ...f,
      styles: f.styles?.includes(style)
        ? f.styles.filter(s => s !== style)
        : [...(f.styles || []), style]
    }));
  };

  const toggleCity = (city) => {
    setForm(f => ({
      ...f,
      cities: f.cities?.includes(city)
        ? f.cities.filter(c => c !== city)
        : [...(f.cities || []), city]
    }));
  };

  // v2.29 : le titre « Profil » reste dans la barre du haut pendant le chargement
  if (loading) return <><MobileHeader title="Profil" /><div className="flex items-center justify-center h-64 text-slate-400">{t('common.loading')}</div></>;
  if (!profile) return (<>
    <MobileHeader title="Profil" />
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-slate-500" data-testid="profile-load-error">
      <span>Le profil n'a pas pu se charger.</span>
      <button onClick={() => { setLoading(true); fetchData(); }} className="h-10 px-4 rounded-xl bg-[#0288D1] text-white font-semibold">Réessayer</button>
    </div>
  </>);

  return (
    <div className="max-w-5xl mx-auto flex flex-col" data-testid="profile-page">
      <MobileHeader title="Profil" actions={!editing
        ? <HeaderAction icon={Edit2} label={t('profile.edit')} onClick={() => setEditing(true)} testId="edit-profile-btn-mobile" />
        : <>
            <HeaderAction icon={X} label="Annuler" onClick={() => { setEditing(false); setForm({ ...profile }); }} />
            <HeaderAction icon={Save} label={t('profile.save')} primary onClick={handleSave} testId="save-profile-btn-mobile" />
          </>} />

      {/* Profile card — toujours en tête, non réorganisable */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4" style={{ order: -1 }}>
        <div className="flex items-start gap-4 mb-4">
          <img
            src={profile.photo_url || 'https://images.unsplash.com/photo-1598898499167-1f52a48743b8?w=200'}
            alt="Profile"
            className="w-16 h-16 rounded-2xl object-cover shrink-0 shadow-sm"
          />
          <div className="flex-1">
            {editing ? (
              <input
                value={form.name || ''}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-lg font-bold mb-2 focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                data-testid="name-input"
              />
            ) : (
              <div className="text-xl font-bold text-slate-900 font-manrope mb-1">{profile.name}</div>
            )}
          </div>
        </div>

        {/* Photo URL when editing */}
        {editing && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-600 mb-1">URL photo</label>
            <input
              value={form.photo_url || ''}
              onChange={e => setForm(f => ({ ...f, photo_url: e.target.value }))}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
              placeholder="https://..."
            />
          </div>
        )}

        {/* Bio */}
        <div className="mb-4">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('profile.bio')}</div>
          {editing ? (
            <textarea
              value={form.bio || ''}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1] resize-none"
              data-testid="bio-input"
            />
          ) : (
            <p className="text-sm text-slate-600">{profile.bio || '—'}</p>
          )}
        </div>

        {/* Styles */}
        <div className="mb-4">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('profile.styles')}</div>
          <div className="flex flex-wrap gap-2">
            {(editing ? STYLES : profile.styles || []).map(style => {
              const active = editing ? form.styles?.includes(style) : true;
              return (
                <button
                  key={style}
                  onClick={editing ? () => toggleStyle(style) : undefined}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-semibold transition-colors',
                    active ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-500',
                    editing && 'cursor-pointer'
                  )}
                >
                  {style}
                </button>
              );
            })}
          </div>
        </div>

        {/* Cities */}
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{t('profile.cities')}</div>
          <div className="flex flex-wrap gap-2">
            {(editing ? CITIES : profile.cities || []).map(city => {
              const active = editing ? form.cities?.includes(city) : true;
              return (
                <button
                  key={city}
                  onClick={editing ? () => toggleCity(city) : undefined}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-semibold transition-colors',
                    active ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500',
                    editing && 'cursor-pointer'
                  )}
                >
                  {city}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Navigation personnalisée ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('nav_config')}>
          <NavConfigPanel />
        </SectionWrapper>
      )}

      {/* ── Guide d'utilisation ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('claude_process')}>
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4" data-testid="claude-process-section">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">🤖 Process de Claude</div>
            <EntryList space="process-claude" withTheme canImport={false} addLabel="Ajouter"
              intro="Les règles que je respecte. Modifie-les quand tu veux : je les relis à chaque passage (9h, 13h)." />
          </div>
        </SectionWrapper>
      )}

      <SectionWrapper {...sectionProps('docs')}>
        <DocsPanel />
      </SectionWrapper>

      {/* Pricing */}
      {/* ── Tarification ── */}
      <SectionWrapper {...sectionProps('pricing_global')}>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">{t('profile.pricing')}</div>
        <div className="space-y-3">
          {[
            ['group_1h30', t('profile.group')],
            ['private_1p_1h', t('profile.private1p')],
            ['private_2p_1h', t('profile.private2p')],
          ].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{label}</span>
              {editing ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={form.pricing?.[key] || ''}
                    onChange={e => setForm(f => ({ ...f, pricing: { ...f.pricing, [key]: parseFloat(e.target.value) } }))}
                    className="w-24 h-9 px-3 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                    data-testid={`pricing-${key}`}
                  />
                  <span className="text-sm text-slate-500">CHF</span>
                </div>
              ) : (
                <span className="font-bold text-slate-900 font-manrope">{(profile.pricing || {})[key]} CHF</span>
              )}
            </div>
          ))}
        </div>
      </div>
      </SectionWrapper>

      {/* ── Formule de recettes ── */}
      <SectionWrapper {...sectionProps('formula')}>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4" data-testid="revenue-formula-section">
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Formule de recettes (collectifs)</div>
        <div className="px-3 py-3 bg-[#0288D1]/5 rounded-xl border border-[#0288D1]/15">
          <p className="text-sm font-semibold text-slate-700 mb-1">
            CA par séance = <span className="text-[#0288D1]">Σ élèves inscrits présents × tarif</span>
          </p>
          <ul className="text-xs text-slate-500 space-y-0.5 mt-1 list-disc list-inside">
            <li>Si un paiement est enregistré ce jour-là : <strong>montant payé</strong></li>
            <li>Sinon : <strong>tarif convenu</strong> (individuel ou cours par défaut)</li>
            <li>Essais et assistants bénévoles : <strong>exclus du CA</strong></li>
          </ul>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-slate-600">Tarif collectif par défaut</span>
          <span className="font-bold text-slate-900 font-manrope">{(profile.pricing || {}).group_1h30 ?? 25} CHF / cours</span>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Modifiable dans <em>Tarifs</em> ci-dessus, ou individuellement par élève dans la fiche du cours.
        </p>
      </div>
      </SectionWrapper>

      {/* ── Matrice Tarifaire ── */}
      <SectionWrapper {...sectionProps('matrix')}>
        <PricingMatrixSection />
      </SectionWrapper>

      {/* ── Lieux ── */}
      <SectionWrapper {...sectionProps('venues')}>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t('profile.venues')}</div>
          <button
            onClick={() => setShowLocForm(true)}
            className="flex items-center gap-1 text-xs text-[#0288D1] font-semibold hover:bg-[#0288D1]/5 px-2 py-1 rounded-lg transition-colors"
            data-testid="add-venue-btn"
          >
            <Plus size={13} /> {t('profile.addVenue')}
          </button>
        </div>

        {showLocForm && (
          <LocationForm onSaved={() => { setShowLocForm(false); fetchData(); }} onCancel={() => setShowLocForm(false)} />
        )}

        <div className="space-y-2">
          {locations.map(loc => (
            <LocationCard key={loc.id} loc={loc} onDeleted={fetchData} />
          ))}
        </div>
      </div>
      </SectionWrapper>

      {/* ── Google Agenda & Tâches ── */}
      <SectionWrapper {...sectionProps('gcal')}>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4" data-testid="gcal-section">
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={16} className="text-[#0288D1]" />
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Google Agenda & Tâches</div>
          {gcalStatus?.connected
            ? <span className="ml-auto flex items-center gap-1 text-xs font-bold text-[#26A69A] bg-[#26A69A]/10 px-2 py-0.5 rounded-full">
                <CheckCircle size={11} /> Connecté
              </span>
            : <span className="ml-auto flex items-center gap-1 text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                <AlertCircle size={11} /> Non connecté
              </span>
          }
        </div>

        {gcalStatus?.connected ? (
          <div className="space-y-3">
            {/* Connected account */}
            {gcalStatus.google_email && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-100">
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-xs font-semibold text-slate-700">{gcalStatus.google_email}</span>
              </div>
            )}

            {/* Tasks scope status */}
            <div className="flex items-center gap-2">
              {gtasksStatus?.connected
                ? <span className="flex items-center gap-1.5 text-xs font-bold text-[#26A69A] bg-[#26A69A]/10 px-2.5 py-1 rounded-full">
                    <CheckCircle size={11} /> Google Tasks activé
                  </span>
                : <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                    <AlertCircle size={11} /> Google Tasks non activé — Reconnecter pour l'activer
                  </span>
              }
            </div>

            <p className="text-xs text-slate-500">
              Vos cours sont synchronisés automatiquement. Utilisez "Synchroniser" pour envoyer tous les cours à venir.
            </p>
            {gcalStatus.connected_at && (
              <p className="text-xs text-slate-400">Connecté le {new Date(gcalStatus.connected_at).toLocaleDateString('fr-CH')}</p>
            )}

            {/* Calendar selector */}
            {gcalCalendars.length > 0 && (
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Calendrier cible</label>
                <select
                  value={gcalCalId}
                  onChange={e => handleGcalCalendarChange(e.target.value)}
                  className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20"
                  data-testid="gcal-calendar-select"
                >
                  <option value="">Calendrier principal</option>
                  {gcalCalendars.map(c => (
                    <option key={c.id} value={c.id}>{c.summary}{c.primary ? ' (principal)' : ''}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={handleGcalSync}
                disabled={gcalSyncing}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0288D1] text-white text-sm font-bold hover:bg-[#0277BE] active:scale-95 transition-all disabled:opacity-50"
                data-testid="gcal-sync-btn"
              >
                <RefreshCw size={14} className={gcalSyncing ? 'animate-spin' : ''} />
                {gcalSyncing ? 'Sync...' : 'Synchroniser'}
              </button>
              {!gtasksStatus?.connected && (
                <button
                  onClick={handleGcalConnect}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600 transition-colors"
                  data-testid="gcal-reconnect-tasks-btn"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>
                  Reconnecter (+ Tasks)
                </button>
              )}
              <button
                onClick={handleGcalDisconnect}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-red-200 text-red-500 text-sm font-bold hover:bg-red-50 transition-colors"
                data-testid="gcal-disconnect-btn"
              >
                <X size={14} /> Déconnecter
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Connectez votre compte Google pour synchroniser vos cours dans Google Agenda et voir vos Google Tasks dans l'app.
            </p>
            <button
              onClick={handleGcalConnect}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border-2 border-slate-200 text-slate-700 text-sm font-bold hover:border-[#0288D1] hover:text-[#0288D1] transition-colors"
              data-testid="gcal-connect-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Connecter Google (Agenda + Tasks)
            </button>
          </div>
        )}
      </div>
      </SectionWrapper>

      {/* ── Sauvegarde ── */}
      <SectionWrapper {...sectionProps('export')}>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4">
        <h2 className="font-bold text-slate-800 font-manrope mb-1">Sauvegarde des données</h2>
        <p className="text-xs text-slate-400 mb-4">
          Exportez toutes vos données en JSON (élèves, cours, présences, forfaits, paiements y compris ceux à attribuer, tarifs, festivals, événements, tâches, habitudes, inscriptions), puis importez-les sur un autre environnement (ex : production). Les comptes et les connexions Google ne sont pas inclus.
        </p>
        <div className="flex gap-3 flex-wrap">
          <button onClick={handleExport} data-testid="export-data-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0288D1]/10 border border-[#0288D1]/20 text-[#0288D1] text-sm font-bold hover:bg-[#0288D1]/20 transition-colors">
            <Download size={15} /> Exporter les données
          </button>
          <label data-testid="import-data-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold hover:bg-emerald-100 transition-colors cursor-pointer">
            <Upload size={15} /> Importer les données
            <input ref={importRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
          </label>
        </div>
        <p className="text-xs text-amber-600 mt-3 bg-amber-50 px-3 py-2 rounded-lg border border-amber-100">
          L'import remplace toutes les données existantes. À utiliser uniquement sur un environnement vide (ex : première mise en production).
        </p>
        </div>
      </SectionWrapper>

      {/* ── Gestion des accès ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('access')}>
          <div className="mb-4"><AccessPanel /></div>
        </SectionWrapper>
      )}

      {/* ── Lien démo ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('demo')}>
          <div className="mb-4"><DemoLinkPanel /></div>
        </SectionWrapper>
      )}

      {/* ── Portail ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('portal')}>
          <div className="mb-4"><PortalPanel profile={profile} onSaved={fetchData} /></div>
        </SectionWrapper>
      )}

      {/* ── v2.27 : Doublons d'élèves ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('duplicates')}>
          <DuplicatesPanel />
        </SectionWrapper>
      )}

      {/* ── v2.30 : Corbeille ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('trash')}>
          <TrashPanel />
        </SectionWrapper>
      )}

      {/* ── Inscriptions portail ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('registrations')}>
          <div className="mb-4"><RegistrationsPanel /></div>
        </SectionWrapper>
      )}

      {/* ── Planificateur ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('planner')}>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-50">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Planificateur partageable</span>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/planificateur`;
                  navigator.clipboard.writeText(url);
                  toast.success('Lien copié !');
                }}
                className="flex items-center gap-1.5 text-xs font-semibold text-[#0288D1] hover:text-[#0277BE] bg-[#0288D1]/8 hover:bg-[#0288D1]/15 px-3 py-1.5 rounded-lg transition-all"
                data-testid="copy-planner-link"
              >
                <Link size={12} /> Copier le lien
              </button>
            </div>
            <div className="p-4">
              <p className="text-xs text-slate-500 mb-3">
                Partagez <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700 font-mono">{window.location.origin}/planificateur</code> dans vos groupes WhatsApp.
                Vos élèves peuvent indiquer leurs disponibilités par cours et séance. Vue admin : toutes les séances (passées incluses).
              </p>
              <PlannerPublic embedded isAdmin />
            </div>
          </div>
        </SectionWrapper>
      )}

      {/* ── API externe ── */}
      {currentUser?.role === 'admin' && (
        <SectionWrapper {...sectionProps('api')}>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4">
            <div className="flex items-center gap-2 px-5 pt-5 pb-3 border-b border-slate-50">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">API Externe</span>
            </div>
            <div className="p-5"><ApiAdminPanel /></div>
          </div>
        </SectionWrapper>
      )}
    </div>
  );
}

// ── NavConfigPanel ── Personnalisation de la navigation ─────────────────────

const ALL_NAV_PATHS = [
  { path: '/planning',         label: 'Planning' },
  { path: '/group-classes',    label: 'Collectifs' },
  { path: '/private-sessions', label: 'Privés' },
  { path: '/students',         label: 'Élèves' },
  { path: '/finance',          label: 'Finance' },
  { path: '/planner',          label: 'Planificateur' },
  { path: '/habits',           label: 'Habitudes' },
  { path: '/festivals',        label: 'Festivals' },
  { path: '/profile',          label: 'Profil' },
];

function NavConfigPanel() {
  const { settings, updateSettings } = useAuth();
  const defaultOrder = ALL_NAV_PATHS.map(n => n.path);

  const [order, setOrder] = useState(() =>
    settings.nav_config || defaultOrder
  );
  const [mobileCount, setMobileCount] = useState(() =>
    settings.nav_mobile_count || 5
  );

  // Sync quand les settings DB arrivent (connexion initiale sur un nouvel appareil)
  useEffect(() => {
    if (settings.nav_config) setOrder(settings.nav_config);
    if (settings.nav_mobile_count) setMobileCount(settings.nav_mobile_count);
  }, [settings.nav_config, settings.nav_mobile_count]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (newOrder, newCount) => {
    // Persiste via context (localStorage immédiat + DB async) — AppShell re-render auto
    updateSettings({ nav_config: newOrder, nav_mobile_count: newCount });
    toast.success('Navigation mise à jour');
  };

  const move = (idx, dir) => {
    const next = [...order];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setOrder(next);
    save(next, mobileCount);
  };

  const getLabel = (path) => ALL_NAV_PATHS.find(n => n.path === path)?.label || path;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4 overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-slate-50 flex items-center justify-between">
        <div>
          <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Navigation personnalisée</div>
          <p className="text-xs text-slate-400 mt-0.5">Réorganisez l'ordre des onglets (mobile et desktop).</p>
        </div>
      </div>
      <div className="p-4">
        <div className="space-y-1.5 mb-4">
          {order.map((path, idx) => (
            <div key={path} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
              <span className={cn('w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                idx < mobileCount ? 'bg-[#0288D1] text-white' : 'bg-slate-200 text-slate-500')}>
                {idx + 1}
              </span>
              <span className="text-sm font-medium text-slate-700 flex-1">{getLabel(path)}</span>
              {idx < mobileCount && (
                <span className="text-[10px] font-bold text-[#0288D1] bg-[#0288D1]/10 px-1.5 py-0.5 rounded-full">Mobile</span>
              )}
              <div className="flex gap-0.5">
                <button onClick={() => move(idx, -1)} disabled={idx === 0}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200 disabled:opacity-30 transition-colors">
                  <ChevronUp size={12} className="text-slate-500" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === order.length - 1}
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200 disabled:opacity-30 transition-colors">
                  <ChevronDown size={12} className="text-slate-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 border-t border-slate-100 pt-3">
          <span className="text-xs font-bold text-slate-500">Onglets mobiles :</span>
          <div className="flex gap-1">
            {[4, 5, 6, 7].map(n => (
              <button key={n} onClick={() => { setMobileCount(n); save(order, n); }}
                className={cn('w-7 h-7 rounded-lg text-xs font-bold transition-colors',
                  mobileCount === n ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')}>
                {n}
              </button>
            ))}
          </div>
          <button onClick={() => { const def = ALL_NAV_PATHS.map(n => n.path); setOrder(def); setMobileCount(5); save(def, 5); }}
            className="ml-auto text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors">
            Réinitialiser
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DocsPanel ── Guide d'utilisation synthétique ───────────────────────────

// v2.28 — Guide d'utilisation : chaque process est un seul schéma (étapes à gauche, cas particuliers à droite, › où)
const DOCS = [
  {
    id: 'registration', icon: '🎓', title: "Inscription d'un nouvel élève", titleColor: 'text-emerald-700',
    flow: [
      { title: "L'élève remplit le formulaire", sub: 'Nom, email, cours souhaités', where: 'Lien public /register',
        cases: [{ title: 'Email déjà connu ?', sub: 'Inscrit directement en essai', where: 'Automatique' }] },
      { title: 'Demande « En attente »', where: 'Profil › Inscriptions portail' },
      { title: 'Tu approuves', sub: 'Fiche créée + inscrite en essai [E]', where: 'Profil › Inscriptions portail › Approuver',
        cases: [{ title: 'Pas de cours choisi ?', sub: 'Ajoute-le à la main', where: 'Collectifs › cours › Ajouter un élève' },
                { title: 'Déjà une fiche (créée au cours) ?', sub: 'Fusionne les deux fiches', where: "Profil › Doublons d'élèves" }] },
    ],
  },
  {
    id: 'attendance', icon: '✅', title: 'Saisie des présences', titleColor: 'text-[#0288D1]',
    flow: [
      { title: 'Ouvrir le cours', where: 'Collectifs › cours (ou Planning › séance)' },
      { title: 'Toucher la pastille de chaque élève', sub: 'A absent → P présent → E essai',
        cases: [{ title: 'Assistant ?', sub: 'Seulement les 14 derniers jours' }] },
      { title: 'Montant figé pour la séance', sub: 'Débité du solde de l’élève', where: 'Automatique',
        cases: [{ title: 'Essai ou absent ?', sub: 'Rien n’est débité' }] },
    ],
  },
  {
    id: 'payment', icon: '💳', title: 'Encaisser et suivre les paiements', titleColor: 'text-violet-700',
    flow: [
      { title: 'Encaisser', sub: 'Montant, moyen, élève', where: '🔍 tape le montant, ou bouton Encaisser',
        cases: [{ title: 'Tu ne sais pas encore qui ?', sub: 'Attribuer plus tard + note (« lunettes »)', where: 'Caisse › Attribuer plus tard' }] },
      { title: 'Le solde se met à jour', sub: 'Paiements − présences', where: 'Automatique',
        cases: [{ title: 'Argent à attribuer ?', sub: 'Choisir l’élève ensuite', where: '🔍 › À attribuer, ou encart Finance' }] },
      { title: 'Relancer ceux qui doivent', sub: 'Message de rappel prérempli', where: 'Finance › Message de rappel' },
    ],
  },
  {
    id: 'private_price', icon: '💶', title: 'Prix des cours privés', titleColor: 'text-teal-700',
    flow: [
      { title: "Prix horaire de l'élève", sub: 'Son profil : ex. 91.6 CHF/h', where: 'Élèves › fiche › Statut tarifaire privé',
        cases: [{ title: 'Pas de profil ou 0 ?', sub: 'Tarif de base 110 CHF/h', where: 'Profil › Tarification' },
                { title: 'Tarif changé ?', sub: 'Prochains privés seulement', where: 'Profil › Matrice tarifaire' }] },
      { title: 'Prix du privé', sub: 'Durée × prix horaire', where: 'Planning › + › Cours privé, ou Privés › +',
        cases: [{ title: 'Sans durée : 1h', sub: 'Prix tapé à la main : ✎ gardé', where: 'Champ CHF de la fenêtre' },
                { title: 'Créé sans élève ?', sub: 'Recalculé à son ajout', where: 'Automatique' }] },
      { title: 'Présence : montant figé', sub: 'Suit le prix du privé s’il change', where: 'Automatique à l’ajout de l’élève',
        cases: [{ title: 'Durée modifiée après ?', sub: 'Nouveau prix proposé, tu choisis', where: 'Planning › clic sur le privé' }] },
      { title: "Ce que l'élève doit", sub: 'Montants figés − paiements', where: 'Finance · fiche de l’élève',
        cases: [{ title: 'Forfait privé ?', sub: 'Heures restantes à la place', where: 'Privés › + › Forfait privé' }] },
    ],
  },
  {
    id: 'duplicates', icon: '👥', title: "Doublons d'élèves", titleColor: 'text-amber-700',
    flow: [
      { title: 'Voir les fiches qui se ressemblent', sub: 'Même nom, prénom sans nom, même tél. ou email', where: "Profil › Doublons d'élèves",
        cases: [{ title: 'Pas un doublon ?', sub: 'Plus jamais proposé', where: 'Bouton « Pas un doublon »' }] },
      { title: 'Choisir la fiche à garder', sub: 'Mon conseil est pré-coché', where: 'Petit rond sur la fiche' },
      { title: 'Fusionner', sub: 'Tout passe sur la fiche gardée, rien en double', where: 'Bouton « Fusionner »',
        cases: [{ title: 'Fiche inutile ?', sub: 'Supprimer (archivée)', where: '« Supprimer cette fiche »' }] },
      { title: 'Erreur ?', sub: 'Tout revient comme avant', where: 'Fusions et suppressions récentes › Annuler' },
    ],
  },
  {
    id: 'balance', icon: '⚖️', title: 'Solde CHF et logique comptable', titleColor: 'text-rose-700',
    flow: [
      { title: 'Crédit : chaque paiement', where: 'Caisse ou fiche élève' },
      { title: 'Débit : chaque présence', sub: 'Au prix figé du jour', where: 'Automatique',
        cases: [{ title: 'Tarif modifié ?', sub: 'Les séances passées ne changent pas' }] },
      { title: 'Solde = crédits − débits', sub: 'Négatif : l’élève doit payer', where: 'Finance · fiche élève' },
    ],
  },
  {
    id: 'planner', icon: '📅', title: 'Planificateur de disponibilités', titleColor: 'text-amber-700',
    flow: [
      { title: 'Copier le lien', where: 'Profil › Planificateur › Copier le lien' },
      { title: 'Partager dans les groupes WhatsApp', sub: 'Les élèves indiquent leurs dispos' },
      { title: 'Voir Leaders / Followers par séance', where: 'Planificateur',
        cases: [{ title: 'Impact ?', sub: 'Aucun sur présences ni facturation' }] },
    ],
  },
  {
    id: 'roles', icon: '🔐', title: 'Rôles et accès utilisateurs', titleColor: 'text-slate-700',
    flow: [
      { title: 'Inviter quelqu’un', where: 'Profil › Gestion des accès' },
      { title: 'Choisir son rôle', sub: 'Admin : tout · Assistant : Collectifs · Observateur : lecture',
        cases: [{ title: 'Assistant', sub: 'Présences des 14 derniers jours' }] },
    ],
  },
];

function DocsPanel() {
  const [openId, setOpenId] = useState(null);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-4 overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b border-slate-50">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Guide d'utilisation</div>
        <p className="text-xs text-slate-400 mt-1">Chaque sujet = un schéma : étapes à gauche, cas particuliers à droite, › où le faire.</p>
      </div>
      <div className="divide-y divide-slate-50">
        {DOCS.map(doc => (
          <div key={doc.id}>
            <button
              onClick={() => setOpenId(openId === doc.id ? null : doc.id)}
              className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50/60 transition-colors"
              data-testid={`doc-toggle-${doc.id}`}
            >
              <span className="text-base w-6 text-center shrink-0">{doc.icon}</span>
              <span className={cn('font-semibold text-sm flex-1', doc.titleColor)}>{doc.title}</span>
              <ChevronDown size={14} className={cn('text-slate-400 shrink-0 transition-transform', openId === doc.id && 'rotate-180')} />
            </button>
            {openId === doc.id && (
              <div className="mx-4 mb-4"><ProcessDiagram flow={doc.flow} /></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const ROLE_LABELS = { admin: 'Admin', assistant: 'Assistant', viewer: 'Observateur' };
const ROLE_COLORS = {
  admin: 'bg-[#0288D1]/10 text-[#0288D1] border-[#0288D1]/20',
  assistant: 'bg-violet-50 text-violet-600 border-violet-200',
  viewer: 'bg-slate-100 text-slate-500 border-slate-200',
};

function AccessPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await api.get('/users');
      setUsers(data);
    } catch { /* silently ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setSaving(true);
    try {
      await api.post('/users/invite', { email: inviteEmail, name: inviteName || inviteEmail, role: inviteRole });
      toast.success(`Accès accordé à ${inviteEmail}`);
      setInviteEmail(''); setInviteName(''); setInviteRole('viewer'); setShowInvite(false);
      fetchUsers();
    } catch (e) { toast.error(e?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    await api.put(`/users/${userId}/role`, { role: newRole });
    toast.success('Rôle mis à jour');
    fetchUsers();
  };

  const handleToggleActive = async (u) => {
    const msg = u.is_active ? `Désactiver ${u.name || u.email} ?` : `Réactiver ${u.name || u.email} ?`;
    if (!window.confirm(msg)) return;
    await api.put(`/users/${u.user_id}/role`, { is_active: !u.is_active });
    toast.success('Compte mis à jour');
    fetchUsers();
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.name || u.email} ?`)) return;
    await api.delete(`/users/${u.user_id}`);
    toast.success('Compte supprimé');
    fetchUsers();
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mt-4" data-testid="access-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield size={17} className="text-[#0288D1]" />
          <h2 className="font-bold text-slate-800 font-manrope">Gestion des accès</h2>
        </div>
        <button onClick={() => setShowInvite(v => !v)} data-testid="invite-user-btn"
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#0288D1] text-white text-xs font-bold hover:bg-[#0277bd] transition-colors">
          <UserPlus size={13} /> Inviter
        </button>
      </div>

      {showInvite && (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-4 space-y-3" data-testid="invite-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Email *</label>
              <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                placeholder="alice@example.com" type="email" data-testid="invite-email-input"
                className="w-full h-9 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Nom</label>
              <input value={inviteName} onChange={e => setInviteName(e.target.value)}
                placeholder="Alice Dupont" data-testid="invite-name-input"
                className="w-full h-9 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Rôle</label>
            <div className="flex gap-2">
              {['viewer', 'assistant', 'admin'].map(r => (
                <button key={r} onClick={() => setInviteRole(r)} data-testid={`role-btn-${r}`}
                  className={cn('px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors',
                    inviteRole === r ? ROLE_COLORS[r] + ' ring-2 ring-offset-1 ring-[#0288D1]/30' : 'bg-white border-slate-200 text-slate-500')}>
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {inviteRole === 'admin' && 'Accès complet à toutes les fonctionnalités.'}
              {inviteRole === 'assistant' && 'Voit les collectifs. Modifie les présences des 2 dernières semaines.'}
              {inviteRole === 'viewer' && 'Voit les collectifs. Lecture seule.'}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowInvite(false)} className="flex-1 h-9 rounded-lg bg-white border border-slate-200 text-slate-600 text-sm font-semibold">Annuler</button>
            <button onClick={handleInvite} disabled={saving || !inviteEmail} data-testid="confirm-invite-btn"
              className="flex-1 h-9 rounded-lg bg-[#0288D1] text-white text-sm font-bold disabled:opacity-50">
              {saving ? '...' : 'Accorder l\'accès'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm py-4 text-center">Chargement...</div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.user_id} data-testid="user-row"
              className={cn('flex items-center gap-3 px-3 py-2.5 rounded-xl border', u.is_active ? 'bg-white border-slate-100' : 'bg-slate-50 border-slate-200 opacity-60')}>
              {u.picture
                ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                : <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0 text-slate-500 text-xs font-bold">
                    {(u.name || u.email)[0].toUpperCase()}
                  </div>
              }
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{u.name || u.email}</div>
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <Mail size={10} />
                  <span className="truncate">{u.email}</span>
                  {!u.is_active && <span className="text-red-400 font-semibold ml-1">· Désactivé</span>}
                </div>
              </div>
              <select value={u.role} onChange={e => handleRoleChange(u.user_id, e.target.value)}
                data-testid={`role-select-${u.email}`}
                className={cn('text-xs font-bold px-2 py-1 rounded-lg border cursor-pointer focus:outline-none', ROLE_COLORS[u.role])}>
                <option value="viewer">Observateur</option>
                <option value="assistant">Assistant</option>
                <option value="admin">Admin</option>
              </select>
              <button onClick={() => handleToggleActive(u)} title={u.is_active ? 'Désactiver' : 'Réactiver'}
                data-testid={`toggle-active-${u.email}`}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors text-amber-400">
                {u.is_active ? <AlertCircle size={14} /> : <CheckCircle size={14} className="text-emerald-500" />}
              </button>
              <button onClick={() => handleDelete(u)} title="Supprimer"
                data-testid={`delete-user-${u.email}`}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors">
                <Trash2 size={13} className="text-red-400" />
              </button>
            </div>
          ))}
          {users.length === 0 && <p className="text-slate-400 text-sm text-center py-3">Aucun utilisateur</p>}
        </div>
      )}
    </div>
  );
}


function DemoLinkPanel() {
  const [tokens, setTokens] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);
  const baseUrl = window.location.origin;

  const fetchTokens = async () => {
    try {
      const data = await api.get('/admin/demo-tokens');
      setTokens(data || []);
    } catch { setTokens([]); }
  };

  useEffect(() => { fetchTokens(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const t = await api.post('/admin/demo-tokens', {});
      toast.success('Lien démo créé — valable 7 jours');
      fetchTokens();
    } catch { toast.error('Erreur lors de la création du lien'); }
    finally { setGenerating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Révoquer ce lien démo ?')) return;
    await api.delete(`/admin/demo-tokens/${id}`);
    toast.success('Lien révoqué');
    fetchTokens();
  };

  const copyLink = (token) => {
    const link = `${baseUrl}/demo/${token}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(token);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mt-4" data-testid="demo-link-section">
      <div className="flex items-center gap-2 mb-1">
        <Link size={16} className="text-[#0288D1]" />
        <h2 className="font-bold text-slate-800 font-manrope">Lien démo partageable</h2>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Génère un lien de prévisualisation sans connexion Google. Le visiteur accède aux cours collectifs en lecture seule avec les données entièrement anonymisées (noms → xxx, montants → 111).
      </p>

      <button onClick={generate} disabled={generating} data-testid="generate-demo-link-btn"
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0288D1] text-white text-sm font-bold hover:bg-[#0277BE] active:scale-[0.98] transition-all disabled:opacity-50">
        <Link size={15} />
        {generating ? 'Génération…' : 'Créer un lien démo (7 jours)'}
      </button>

      {tokens.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Liens actifs</p>
          {tokens.map(t => {
            const link = `${baseUrl}/demo/${t.token}`;
            const expiresAt = new Date(t.expires_at).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
            return (
              <div key={t.id} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500 font-mono truncate">{link}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Expire le {expiresAt}</p>
                </div>
                <button onClick={() => copyLink(t.token)} data-testid="copy-demo-link-btn"
                  className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors shrink-0"
                  title="Copier le lien">
                  {copied === t.token ? <Check size={14} className="text-emerald-500" /> : <Link size={14} className="text-slate-400" />}
                </button>
                <button onClick={() => revoke(t.id)} data-testid="revoke-demo-link-btn"
                  className="p-1.5 rounded-lg hover:bg-red-50 transition-colors shrink-0"
                  title="Révoquer ce lien">
                  <XCircle size={14} className="text-red-400" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function LocationCard({ loc, onDeleted }) {
  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce lieu ?')) return;
    await api.delete(`/locations/${loc.id}`);
    toast.success('Lieu supprimé');
    onDeleted();
  };
  return (
    <div className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2.5" data-testid="location-card">
      <div className="flex items-center gap-2">
        <MapPin size={15} className="text-[#0288D1]" />
        <div>
          <div className="text-sm font-semibold text-slate-800">{loc.venue_name}</div>
          <div className="text-xs text-slate-500">{loc.city}{loc.description ? ` · ${loc.description}` : ''}</div>
        </div>
      </div>
      <button onClick={handleDelete} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors" data-testid="delete-location-btn">
        <Trash2 size={13} className="text-red-400" />
      </button>
    </div>
  );
}

function LocationForm({ onSaved, onCancel }) {
  const { t } = useLang();
  const [form, setForm] = useState({ city: '', venue_name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.city || !form.venue_name) return;
    setSaving(true);
    try {
      await api.post('/locations', form);
      toast.success('Lieu ajouté');
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 mb-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">{t('common.city')}</label>
          <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
            className="w-full h-9 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
            placeholder="Genève"
            data-testid="location-city-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">{t('common.venue')}</label>
          <input value={form.venue_name} onChange={e => setForm(f => ({ ...f, venue_name: e.target.value }))}
            className="w-full h-9 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
            placeholder="PLO"
            data-testid="location-venue-input"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">{t('common.desc')}</label>
        <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          className="w-full h-9 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
          placeholder="ex: Piscine des Lombards"
        />
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 h-9 rounded-lg bg-white border border-slate-200 text-slate-600 text-sm font-semibold">{t('common.cancel')}</button>
        <button onClick={handleSave} disabled={saving} className="flex-1 h-9 rounded-lg bg-[#0288D1] text-white text-sm font-bold">{saving ? '...' : t('common.save')}</button>
      </div>
    </div>
  );
}


/* ── PortalPanel ── Configuration du portail d'inscription public */
function PortalPanel({ profile, onSaved }) {
  const [active, setActive] = useState(profile?.registration_active !== false);
  const [message, setMessage] = useState(profile?.registration_message || '');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const publicUrl = window.location.origin + '/register';

  useEffect(() => {
    setActive(profile?.registration_active !== false);
    setMessage(profile?.registration_message || '');
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/teacher', { registration_active: active, registration_message: message });
      toast.success('Portail mis à jour');
      onSaved();
    } finally { setSaving(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mt-4" data-testid="portal-panel">
      <div className="flex items-center gap-2 mb-4">
        <Globe2 size={17} className="text-[#0288D1]" />
        <h2 className="font-bold text-slate-800 font-manrope">Portail d'inscription public</h2>
      </div>

      {/* Link + toggle */}
      <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200 mb-4">
        <Link size={13} className="text-slate-400 shrink-0" />
        <span className="flex-1 text-xs text-slate-600 truncate font-mono">{publicUrl}</span>
        <button onClick={copyLink} data-testid="copy-portal-link"
          className={cn("text-xs px-2 py-1 rounded-lg font-semibold transition-colors",
            copied ? "bg-emerald-100 text-emerald-600" : "bg-[#0288D1]/10 text-[#0288D1] hover:bg-[#0288D1]/20")}>
          {copied ? 'Copié !' : 'Copier'}
        </button>
        <a href={publicUrl} target="_blank" rel="noreferrer"
          className="text-xs px-2 py-1 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 font-semibold transition-colors">
          Ouvrir
        </a>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-semibold text-slate-700">Inscriptions ouvertes</span>
        <button onClick={() => setActive(v => !v)} data-testid="portal-toggle"
          className={cn("w-11 h-6 rounded-full transition-colors relative",
            active ? "bg-[#0288D1]" : "bg-slate-200")}>
          <div className={cn("w-4 h-4 bg-white rounded-full absolute top-1 transition-transform shadow-sm",
            active ? "translate-x-6" : "translate-x-1")} />
        </button>
      </div>

      <div className="mb-4">
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          Message d'accueil (affiché sur la page publique)
        </label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Bienvenue ! Je suis professeur de Bachata et Brazilian Zouk à Genève depuis 10 ans…"
          rows={4} data-testid="portal-message"
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1] resize-none"
        />
      </div>

      <button onClick={handleSave} disabled={saving} data-testid="save-portal-btn"
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0288D1] text-white text-sm font-bold hover:bg-[#0277bd] transition-colors disabled:opacity-50">
        <Save size={14} />
        {saving ? 'Sauvegarde…' : 'Sauvegarder'}
      </button>
    </div>
  );
}

/* ── RegistrationsPanel ── Gestion des demandes d'inscription */
const STATUS_CFG = {
  pending:  { label: 'En attente',  cls: 'bg-amber-50 text-amber-600 border-amber-200' },
  approved: { label: 'Approuvé',    cls: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  rejected: { label: 'Refusé',      cls: 'bg-red-50 text-red-500 border-red-200' },
};

function RegistrationsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);

  const fetchReqs = async () => {
    setLoading(true);
    try {
      const data = await api.get('/admin/registrations');
      setRequests(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchReqs(); }, []);

  const handleApprove = async (id) => {
    try {
      await api.put(`/admin/registrations/${id}/approve`);
      toast.success('Élève créé et inscrit !');
      fetchReqs();
    } catch (e) { toast.error(e?.detail || 'Erreur'); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Refuser cette demande ?')) return;
    await api.put(`/admin/registrations/${id}/reject`, {});
    toast.success('Demande refusée');
    fetchReqs();
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/registrations/${id}`);
    fetchReqs();
  };

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mt-4" data-testid="registrations-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <UserPlus size={17} className="text-[#0288D1]" />
          <h2 className="font-bold text-slate-800 font-manrope">
            Demandes d'inscription
            {pendingCount > 0 && (
              <span className="ml-2 text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">{pendingCount}</span>
            )}
          </h2>
        </div>
        <div className="flex gap-1">
          {['pending', 'approved', 'rejected', 'all'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={cn('px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors',
                filter === s ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')}>
              {s === 'all' ? 'Tout' : STATUS_CFG[s]?.label}
              {s === 'pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm text-center py-4">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-400 text-sm text-center py-6">
          {filter === 'pending' ? 'Aucune demande en attente' : 'Aucune demande'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(req => {
            const isOpen = expandedId === req.id;
            return (
              <div key={req.id} data-testid="registration-row"
                className={cn('border rounded-xl transition-colors', isOpen ? 'border-[#0288D1]/30 bg-[#0288D1]/2' : 'border-slate-100 hover:border-slate-200')}>

                {/* ── Header (toujours visible) — clic pour expand ── */}
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : req.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left"
                  data-testid={`registration-toggle-${req.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 text-sm leading-tight">
                      {req.first_name} {req.last_name}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {new Date(req.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {req.course_names?.length > 0 && (
                        <span className="ml-2 text-[#0288D1]">{req.course_names.join(', ')}</span>
                      )}
                    </div>
                  </div>
                  {/* Rôle badge si renseigné */}
                  {req.dance_role && (
                    <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0',
                      req.dance_role === 'leader' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-pink-50 text-pink-600 border-pink-200')}>
                      {req.dance_role === 'leader' ? 'Leader' : 'Follower'}
                    </span>
                  )}
                  <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full border shrink-0', STATUS_CFG[req.status]?.cls)}>
                    {STATUS_CFG[req.status]?.label}
                  </span>
                  <ChevronDown size={14} className={cn('text-slate-400 shrink-0 transition-transform', isOpen && 'rotate-180')} />
                </button>

                {/* ── Détails (visible uniquement si ouvert) ── */}
                {isOpen && (
                  <div className="px-4 pb-4 border-t border-slate-100 mt-0">
                    <div className="pt-3 space-y-3">

                      {/* Coordonnées */}
                      <div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Coordonnées</div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
                          {req.email && <span className="flex items-center gap-1"><Mail size={10} className="text-slate-400" />{req.email}</span>}
                          {req.phone && <span className="flex items-center gap-1"><span className="text-slate-400 text-[10px]">✆</span>{req.phone}</span>}
                          {req.instagram && <span className="flex items-center gap-1"><span className="text-slate-400 text-[10px]">@</span>{req.instagram.replace('@', '')}</span>}
                        </div>
                      </div>

                      {/* Profil danse */}
                      <div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Profil danse</div>
                        <div className="flex flex-wrap gap-2">
                          {req.dance_role && (
                            <span className={cn('text-xs font-semibold px-2.5 py-0.5 rounded-full border',
                              req.dance_role === 'leader' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-pink-50 text-pink-600 border-pink-200')}>
                              {req.dance_role === 'leader' ? 'Leader' : 'Follower'}
                            </span>
                          )}
                          {req.dance_background && (
                            <span className="text-xs bg-[#0288D1]/8 text-[#0288D1] px-2.5 py-0.5 rounded-full border border-[#0288D1]/20">{req.dance_background}</span>
                          )}
                          {req.experience_years > 0 && (
                            <span className="text-xs bg-slate-100 text-slate-500 px-2.5 py-0.5 rounded-full">{req.experience_years} an{req.experience_years > 1 ? 's' : ''} d'expérience</span>
                          )}
                          {!req.dance_role && !req.dance_background && !req.experience_years && (
                            <span className="text-xs text-slate-400 italic">Non renseigné</span>
                          )}
                        </div>
                      </div>

                      {/* Cours souhaités */}
                      {req.course_names?.length > 0 && (
                        <div>
                          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Cours souhaités</div>
                          <div className="flex gap-1 flex-wrap">
                            {req.course_names.map((name, i) => (
                              <span key={i} className="text-xs bg-violet-50 text-violet-600 px-2.5 py-0.5 rounded-full border border-violet-200">{name}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Message */}
                      {req.message && (
                        <div>
                          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Message</div>
                          <p className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 italic">"{req.message}"</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                        {req.status === 'pending' ? (
                          <div className="flex gap-2 ml-auto">
                            <button onClick={() => handleReject(req.id)} data-testid="reject-registration-btn"
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors border border-red-200">
                              <XCircle size={12} /> Refuser
                            </button>
                            <button onClick={() => handleApprove(req.id)} data-testid="approve-registration-btn"
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors">
                              <Check size={12} /> Approuver
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => handleDelete(req.id)} className="ml-auto text-xs text-slate-400 hover:text-red-400 transition-colors flex items-center gap-1">
                            <Trash2 size={13} /> Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
